import { memo, type FC } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";

type MatchDonutChartProps = {
  matched: number;
  unmatched: number;
  invalid: number;
  total: number;
  loading?: boolean;
  size?: number;
};

export const MatchDonutChart: FC<MatchDonutChartProps> = memo(
  ({ matched, unmatched, total, loading = false, size = 120 }) => {
    const matchedPct = total > 0 ? (matched / total) * 100 : 0;
    const unmatchedPct = total > 0 ? (unmatched / total) * 100 : 0;
    const stop1 = matchedPct;
    const stop2 = matchedPct + unmatchedPct;

    const innerOffset = 8;
    const innerSize = size - innerOffset * 2;

    if (loading) {
      return (
        <Stack alignItems="center">
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Skeleton
              variant="circular"
              width={size}
              height={size}
              animation="wave"
            />
            <Box
              sx={{
                position: "absolute",
                top: innerOffset,
                left: innerOffset,
                right: innerOffset,
                bottom: innerOffset,
                borderRadius: "50%",
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography variant="h5" color="text.disabled">
                —%
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Match
              </Typography>
            </Box>
          </Box>
        </Stack>
      );
    }

    return (
      <Stack alignItems="center">
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: "50%",
              background: (theme) =>
                `conic-gradient(
                  ${theme.palette.success.main} 0% ${stop1}%,
                  ${theme.palette.warning.main} ${stop1}% ${stop2}%,
                  ${theme.palette.error.main} ${stop2}% 100%
                )`,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: innerOffset,
              left: innerOffset,
              width: innerSize,
              height: innerSize,
              borderRadius: "50%",
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography variant="h4" component="div" fontWeight={600}>
              {Math.round(matchedPct)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Match
            </Typography>
          </Box>
        </Box>
      </Stack>
    );
  }
);

MatchDonutChart.displayName = "MatchDonutChart";
